/**
 * Camera for the isometric pixel world.
 * Handles pan, zoom, and coordinate transforms between world and screen space.
 */

export class Camera {
  /** World position (center of view). */
  x = 0;
  y = 0;

  /** Zoom level. 1.0 = 100%. Range: 0.5 to 4.0. */
  zoom = 2;

  // Low enough that a wide scene can fully fit a narrow (phone) viewport via
  // fitToScene; on larger viewports the fit zoom is well above this anyway.
  private static readonly MIN_ZOOM = 0.3;
  private static readonly MAX_ZOOM = 4;
  private static readonly ZOOM_SPEED = 0.1;

  /** Convert screen pixel coords to world coords. */
  screenToWorld(sx: number, sy: number, canvasW: number, canvasH: number): { x: number; y: number } {
    return {
      x: (sx - canvasW / 2) / this.zoom + this.x,
      y: (sy - canvasH / 2) / this.zoom + this.y,
    };
  }

  /** Convert world coords to screen pixel coords. */
  worldToScreen(wx: number, wy: number, canvasW: number, canvasH: number): { x: number; y: number } {
    return {
      x: (wx - this.x) * this.zoom + canvasW / 2,
      y: (wy - this.y) * this.zoom + canvasH / 2,
    };
  }

  /** Apply camera transform to a canvas 2D context. Call before drawing. */
  applyTransform(ctx: CanvasRenderingContext2D, canvasW: number, canvasH: number): void {
    ctx.translate(canvasW / 2, canvasH / 2);
    ctx.scale(this.zoom, this.zoom);
    ctx.translate(-this.x, -this.y);
  }

  /** Pan by screen-space delta (e.g. mouse drag distance). */
  pan(dxScreen: number, dyScreen: number): void {
    this.x -= dxScreen / this.zoom;
    this.y -= dyScreen / this.zoom;
  }

  /** Zoom toward a screen point (e.g. mouse position). */
  zoomAt(screenX: number, screenY: number, delta: number, canvasW: number, canvasH: number): void {
    // World position under the cursor before zoom
    const before = this.screenToWorld(screenX, screenY, canvasW, canvasH);

    // Adjust zoom
    const direction = delta > 0 ? -1 : 1;
    this.zoom = Math.max(
      Camera.MIN_ZOOM,
      Math.min(Camera.MAX_ZOOM, this.zoom * (1 + direction * Camera.ZOOM_SPEED)),
    );

    // World position under the cursor after zoom
    const after = this.screenToWorld(screenX, screenY, canvasW, canvasH);

    // Adjust camera to keep the cursor point fixed
    this.x += before.x - after.x;
    this.y += before.y - after.y;
  }

  /** Center camera to fit a bounding box in world coordinates. */
  fitToScene(
    bounds: { minX: number; minY: number; maxX: number; maxY: number },
    canvasW: number,
    canvasH: number,
    padding = 60,
  ): void {
    this.x = (bounds.minX + bounds.maxX) / 2;
    this.y = (bounds.minY + bounds.maxY) / 2;

    const sceneW = bounds.maxX - bounds.minX + padding * 2;
    const sceneH = bounds.maxY - bounds.minY + padding * 2;

    if (sceneW > 0 && sceneH > 0) {
      this.zoom = Math.max(
        Camera.MIN_ZOOM,
        Math.min(Camera.MAX_ZOOM, Math.min(canvasW / sceneW, canvasH / sceneH)),
      );
    }
  }
}
